import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { createSlug } from "../../context/utils";
import useAnimeStore from "../../Store/AnimeStore";

const SearchIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const AnimeSearchBar = ({ onResultClick }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [error] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const allAnime = useAnimeStore((state) => state.allAnimes);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (allAnime) {
      if (searchTerm.trim() === "") {
        setResults([]);
        return;
      }

      const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);

      const filtered = allAnime
        .map((anime) => {
          const titleObj = anime.title;
          const title = (typeof titleObj === 'object' ? (titleObj.english || titleObj.romaji || titleObj.native) : titleObj || "").toLowerCase();
          const year = anime.animeyear?.toString() || "";
          const duration = anime.animeduration?.toString() || "";
          let score = 0;

          // Exact title match
          if (title === searchTerm.toLowerCase()) {
            score += 100;
          }

          // Title word matches
          searchWords.forEach((word) => {
            if (title.startsWith(word)) {
              score += 30;
            } else if (title.includes(` ${word}`)) {
              score += 20;
            } else if (title.includes(word)) {
              score += 10;
            }

            // Year match
            if (year.includes(word)) {
              score += 15;
            }

            // Duration match
            if (duration.includes(word)) {
              score += 5;
            }
          });

          return { ...anime, score };
        })
        .filter((anime) => anime.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      setResults(filtered);
    }
  }, [searchTerm, allAnime]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsFocused(false);
    } else if (e.key === "Enter" && results.length > 0) {
      window.location.href = `/anime/${createSlug(results[0].title)}`;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "24m";
    return minutes > 60
      ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
      : `${minutes}m`;
  };

  return (
    <div className="relative max-w-lg w-full" ref={searchRef}>
      {error && <div className="text-red-500 text-center mb-2">{error}</div>}
      <div
        className={`relative flex items-center h-11 rounded-full transition-all duration-300 ease-in-out bg-[#211d1dc4] border ${isFocused
          ? "border-white shadow-[0_2px_8px_rgba(255,107,107,0.2)]"
          : "border-transparent"
          }`}
      >
        <SearchIcon className="absolute left-4 h-5 w-5 text-white/50" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search anime, movies, and more..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          className="w-full h-full bg-transparent text-white placeholder-white/50 text-sm pl-12 pr-4 rounded-full outline-none"
          aria-haspopup="listbox"
        />
      </div>

      {isFocused && results.length > 0 && (
        <div
          className="absolute top-full mt-2 w-full bg-[#151617] rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.1)] z-[1000]"
          role="listbox"
          aria-label="Search results"
        >
          <div className="max-h-[400px] overflow-y-auto">
            {results.map((anime) => (
              <Link
                key={anime.id}
                to={`/anime/${createSlug(anime.title)}?animeID=${anime.id}`}
                className="flex items-center p-3 gap-3 transition-colors duration-200 hover:bg-[#202020]"
                role="option"
                aria-selected="false"
                onClick={() => {
                  if (onResultClick) onResultClick();
                  setIsFocused(false);
                }}
              >
                <img
                  src={anime.imageUrl}
                  alt={anime.title?.english || anime.title?.romaji || anime.title?.native || anime.title}
                  className="w-12 h-16 object-cover rounded-md flex-shrink-0"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/80x120?text=No+Image";
                  }}
                />
                <div className="flex-1 min-w-0">
                  <h4 className=" text-white text-sm truncate">
                    {anime.title?.english || anime.title?.romaji || anime.title?.native || anime.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-white/60 mt-1">
                    <span>{anime.animeyear || "2024"}</span>
                    <span>•</span>
                    <span>{formatDuration(anime.animeduration)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {anime.genres?.slice(0, 3).map((genre) => (
                      <span
                        key={genre}
                        className="bg-[#1e1e20] px-2 py-0.5 rounded-full text-xs text-white"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimeSearchBar;
